import client from 'client'
import type {
  GetAssetRegisterListAssetRegisterGetQueryResponse,
  GetAssetRegisterListAssetRegisterGetQueryParams,
  GetAssetRegisterListAssetRegisterGet422,
} from '../../types/GetAssetRegisterListAssetRegisterGet.ts'
import type { QueryKey, QueryObserverOptions, UseQueryResult } from '@tanstack/react-query'
import type { RequestConfig, ResponseErrorConfig, ResponseConfig } from 'client'
import { queryOptions, useQuery } from '@tanstack/react-query'

export const getAssetRegisterListAssetRegisterGetQueryKey = (params?: GetAssetRegisterListAssetRegisterGetQueryParams) =>
  [{ url: '/asset_register' }, ...(params ? [params] : [])] as const

export type GetAssetRegisterListAssetRegisterGetQueryKey = ReturnType<typeof getAssetRegisterListAssetRegisterGetQueryKey>

/**
 * @summary Get Asset Register List
 * {@link /asset_register}
 */
export async function getAssetRegisterListAssetRegisterGet(
  params?: GetAssetRegisterListAssetRegisterGetQueryParams,
  config: Partial<RequestConfig> & { client?: typeof client } = {},
) {
  const { client: request = client, ...requestConfig } = config

  const res = await request<GetAssetRegisterListAssetRegisterGetQueryResponse, ResponseErrorConfig<GetAssetRegisterListAssetRegisterGet422>, unknown>({
    method: 'GET',
    url: `/asset_register`,
    params,
    ...requestConfig,
  })
  return res
}

export function getAssetRegisterListAssetRegisterGetQueryOptions(
  params?: GetAssetRegisterListAssetRegisterGetQueryParams,
  config: Partial<RequestConfig> & { client?: typeof client } = {},
) {
  const queryKey = getAssetRegisterListAssetRegisterGetQueryKey(params)
  return queryOptions<
    ResponseConfig<GetAssetRegisterListAssetRegisterGetQueryResponse>,
    ResponseErrorConfig<GetAssetRegisterListAssetRegisterGet422>,
    ResponseConfig<GetAssetRegisterListAssetRegisterGetQueryResponse>,
    typeof queryKey
  >({
    queryKey,
    queryFn: async ({ signal }) => {
      config.signal = signal
      return getAssetRegisterListAssetRegisterGet(params, config)
    },
  })
}

/**
 * @summary Get Asset Register List
 * {@link /asset_register}
 */
export function useGetAssetRegisterListAssetRegisterGet<
  TData = ResponseConfig<GetAssetRegisterListAssetRegisterGetQueryResponse>,
  TQueryData = ResponseConfig<GetAssetRegisterListAssetRegisterGetQueryResponse>,
  TQueryKey extends QueryKey = GetAssetRegisterListAssetRegisterGetQueryKey,
>(
  params?: GetAssetRegisterListAssetRegisterGetQueryParams,
  options: {
    query?: Partial<
      QueryObserverOptions<
        ResponseConfig<GetAssetRegisterListAssetRegisterGetQueryResponse>,
        ResponseErrorConfig<GetAssetRegisterListAssetRegisterGet422>,
        TData,
        TQueryData,
        TQueryKey
      >
    >
    client?: Partial<RequestConfig> & { client?: typeof client }
  } = {},
) {
  const { query: queryOptions, client: config = {} } = options ?? {}
  const queryKey = queryOptions?.queryKey ?? getAssetRegisterListAssetRegisterGetQueryKey(params)

  const query = useQuery({
    ...(getAssetRegisterListAssetRegisterGetQueryOptions(params, config) as unknown as QueryObserverOptions),
    queryKey,
    ...(queryOptions as unknown as Omit<QueryObserverOptions, 'queryKey'>),
  }) as UseQueryResult<TData, ResponseErrorConfig<GetAssetRegisterListAssetRegisterGet422>> & { queryKey: TQueryKey }

  query.queryKey = queryKey as TQueryKey

  return query
}